import { AppNav } from "@app/page-components";
import ReactDOM from "react-dom";
import { Constant } from '../const';
import DesignerSetting from "../DesignerSetting";
import NoteNodeModel from "../models/nodes/NoteNodeModel";
import CanvasTabManager from "./CanvasTabManager";
import CreateObjects from "./CreateObjects";
import Utility from "./Utility";
import { ChannelSelector, LanguageSelector, TtsVoiceSelector, UserTypeSelector } from "../form_features";
import './jquery-plugins/on-resize';
import { getGlobalSettings } from "../stores";

export default class Config {
  /**
   * App configurations
   * @module Config
   * @param {AppManager} apm
   */
  constructor(apm) {
    this.apm = apm;
    this.initDebugConfig();
  }
  
  /**
   * Set debug configurations
   */
  initDebugConfig() {
    this.debug = {};
    this.debug.enabled = false;
    this.debug[Constant.k.DEBUG_FORM] = true;
    this.debug[Constant.k.DEBUG_TREE] = false;
    this.debug[Constant.k.DEBUG_STORAGE] = false;
    this.debug[Constant.k.DEBUG_CACHE] = true;
    this.debug[Constant.k.DEBUG_UI] = false;
  }

  /**
   * Initialize layout and treeviews
   */
  initLayoutConfig() {
    this.initLayout();
    this.initTrees();
    const treeDom = $("#tl_container");

    // Load application tree with delay
    clearInterval(this.updateTimer);
    this.updateTimer = setInterval(() => {
      var treeObj = treeDom.jstree();
      if (treeObj) {
        clearInterval(this.updateTimer);
        treeObj.settings.core.data = window.apm.getTree();
        let sortfunc = function(a, b){
          let a1 = this.get_node(a);
          let b1 = this.get_node(b);
          if (a1.icon === b1.icon){
            return (a1.text > b1.text) ? 1 : -1;
          } else {
            return (a1.icon > b1.icon) ? 1 : -1;
          }
        };
        treeObj.settings.sort = sortfunc;
        treeObj.refresh();
      }
        
      }, 200);
  }

  /**
   * Initialize layout for the entire editor
   */
  initLayout() {
    const rootModule = window.apm.getNode(window.apm.serialized.rootModule);
    const { isOmniChannel } = window.apm.serialized.omniChannel;
    const toolbarButtons = [
      {
        type: "button",
        id: "icon-check-square",
        img: "fe icon-check-square",
        tooltip: window.l.toolbar.validate_application
      },
      {
        type: "button",
        id: "icon-validate",
        img: "fe icon-check-circle",
        tooltip: window.l.toolbar.validate_diagram
      },
      {
        type: "button",
        id: "icon-download",
        img: "fe icon-download",
        tooltip: window.l.toolbar.download_module
      },
      {
        type: "button",
        id: "icon-capture",
        img: "fe icon-aperture",
        tooltip: window.l.toolbar.download_image
      },
      {
        type: "button",
        id: "icon-show-disabled",
        img: "fe icon-x-circle",
        tooltip: "Show disabled items"
      },
      { type: "break" },
      {
        type: "button",
        id: "icon-undo",
        img: "fe icon-corner-up-left",
        tooltip: window.l.toolbar.undo
      },
      {
        type: "button",
        id: "icon-redo",
        img: "fe icon-corner-up-right",
        tooltip: window.l.toolbar.redo
      },
      {
        type: "button",
        id: "icon-copy",
        img: "fe icon-copy",
        tooltip: window.l.toolbar.copy
      },
      {
        type: "button",
        id: "icon-paste",
        img: "fe icon-clipboard",
        tooltip: window.l.toolbar.paste
      },
      {
        type: "button",
        id: "icon-delete",
        img: "fe icon-trash-2",
        tooltip: window.l.toolbar.delete
      },
      { type: "break" },

      {
        type: "button",
        id: "icon-message-square",
        img: "fe icon-message-square",
        tooltip: window.l.toolbar.create_prompt_drag
      },
      {
        type: "button",
        id: "icon-mic",
        img: "fe icon-mic",
        tooltip: window.l.toolbar.create_input_drag
      },
      {
        type: "button",
        id: "icon-cpu",
        img: "fe icon-cpu",
        tooltip: window.l.toolbar.create_custom_drag
      },
      {
        type: "button",
        id: "icon-package",
        img: "fe icon-package",
        tooltip: window.l.toolbar.create_module_drag
      },
      {
        type: "button",
        id: "icon-note",
        img: "fe icon-file-text",
        tooltip: window.l.toolbar.create_note_drag
      },
      { type: "break" },
      {
        type: "button",
        id: "icon-zoom-out",
        img: "fe icon-zoom-out",
        tooltip: window.l.toolbar.zoom_out
      },
      {
        type: "html",
        id: "value-zoom",
        html:
          '<input type="text" id="value-zoom" class="form-component marginal center-text" size="4" value="100%"/>'
      },
      {
        type: "button",
        id: "icon-zoom-in",
        img: "fe icon-zoom-in",
        tooltip: window.l.toolbar.zoom_in
      },
      {
        type: "button",
        id: "icon-toggle-disabled-elements",
        img: "fe icon-eye",
        tooltip: "toggle disabled elements view"
      },
      {
        type: "button",
        id: "icon-center-canvas",
        img: "fe icon-crosshair",
        tooltip: "Centers canvas"
      },
      { type: "spacer" }
    ];
    
    var toolbarItems = [];
    const closePanel = "window.apm.postEventToCurrentView({ target: 'request-hide-property-panel'})";
    
    if (DesignerSetting.s.env !== "PROD") {
      // Show "Reset demo" for non-prod
      toolbarItems = [
        ...toolbarButtons,
        // keep this for setting perf test in UI, dev only
        //{type: 'check', id: 'icon-toggle-perf-test', icon: 'fe icon-clock', text: 'Perf Test'},
        {
          type: "html",
          id: "dev-tools",
          html: '<div class="toolbar-dev-tools">'
          + '<div id="channel_selector_toolbar" class="dropdown" onclick="'+closePanel+'"></div>'
          + '<div id="lang_selector_toolbar" class="dropdown" onclick="'+closePanel+'"></div>'
          + '<div id="tts_selector_toolbar" class="dropdown" onclick="'+closePanel+'"></div>'
          + '<div id="role_selector_toolbar" class="dropdown user_type" onclick="'+closePanel+'"></div>'
          + '</div>'
        },
        {
          type: "button",
          id: "author",
          text: window.apm.getDesignerAuthor(),
          tooltip: window.l.toolbar.author
        }
      ];
    } else {
      toolbarItems = [...toolbarButtons];
    }
    
    this.layout = {
      name: "layout",
      padding: 0,
      panels: [
        { type: "top", size: 60 },
        {
          type: "main",
          overflow: "hidden",
          opacity: 0,
          tabs: {
            active: rootModule.id,
            tabs: [
              {
                id: rootModule.id,
                moduleId: rootModule.id,
                data: rootModule.data,
                text: rootModule.name
              }
            ],
            onClick: function (event) {
              CanvasTabManager.handleTabsOnClick(event);
            },
            onClose: function (event) {
              CanvasTabManager.handleTabsOnClose(event);
            },
            onRender: function (event) {
              CanvasTabManager.initTabs(event);
            }
          },
          toolbar: {
            name: "toolbar",
            items: toolbarItems,
            onClick: function (event) {
              switch (event.target) {
                case "icon-validate":
                  window.apm.postEventToCurrentView({
                    target: "request-validate"
                  });
                  break;
                case "icon-check-square":
                  window.apm.postEventToCurrentView({
                    target: "request-application-validation"
                  });
                  break;
                case "icon-download":
                  window.apm.postEventToCurrentView({
                    target: "request-download-json"
                  });
                  break;
                case "icon-capture":
                  window.apm.postEventToCurrentView({
                    target: "request-download-image"
                  });
                  break;
                case "icon-show-disabled":
                  window.apm.openDisabledItemsWindow();
                case "icon-redo":
                  window.apm.postEventToCurrentView({ target: "request-redo" });
                  break;
                case "icon-undo":
                  window.apm.postEventToCurrentView({ target: "request-undo" });
                  break;
                case "icon-copy":
                  window.apm.postEventToCurrentView({ target: "request-copy" });
                  break;
                case "icon-paste":
                  window.apm.postEventToCurrentView({
                    target: "request-paste"
                  });
                  break;
                case "icon-delete":
                  window.apm.postEventToCurrentView({
                    target: "request-delete"
                  });
                  break;
                case "icon-zoom-in":
                  window.apm.postEventToCurrentView({
                    target: "request-zoom-in"
                  });
                  break;
                case "icon-zoom-out":
                  window.apm.postEventToCurrentView({
                    target: "request-zoom-out"
                  });
                  break;
                case "icon-toggle-disabled-elements":
                  window.apm.setDisplayPrefs("toggle-disabled-elements");
                  break;
                case "icon-center-canvas":
                  window.apm.getCurrentDiagramWidget().setCanvasCenter();
                  break;
                case "icon-toggle-perf-test":
                    window.apm.togglePerfTestFlag();
                    break;
                case "icon-note":
                case "icon-cpu":
                case "icon-message-square":
                case "icon-mic":
                case "icon-package":
                case "dropdown-language":
                case "dropdown-view":
                  break;
              }
            },
            onRender(){
              
              setTimeout(() => {
                if(isOmniChannel) {
                  ReactDOM.render(<ChannelSelector />, document.getElementById("channel_selector_toolbar"));
                }
                ReactDOM.render(<LanguageSelector />, document.getElementById("lang_selector_toolbar"));
                ReactDOM.render(<TtsVoiceSelector />, document.getElementById("tts_selector_toolbar"));
                ReactDOM.render(<UserTypeSelector />, document.getElementById("role_selector_toolbar"));

                const { defaultChannel } = window.apm.serialized.rootSettings;
                const { setSettings } = getGlobalSettings();

                // Set default channel in the global settings
                if(defaultChannel){
                  setSettings({ channel: defaultChannel });
                }
                
              }, 250);
              
            }
          }
        },
        { type: "bottom", size: 22 }
      ],
      // Update the bottom panel top CSS property due to the adjustments
      // made for layout_layout_panel_main (see override.scss).
      onResize: (event) => {
        // event.done allows to define a callback that's called
        // once the event is fully processed.
        // https://w2ui.com/web/docs/1.5/utils/events
        event.done(() => {
          const panelBottomElement = $('#layout_layout_panel_bottom');
          const currentTop = parseFloat(panelBottomElement.css('top'));

          // 6px makes up the extra top added to layout_layout_panel_main in override.scss
          const newTop = `${(currentTop + 6)}px`;
          panelBottomElement.css('top', newTop);
        });
      },
    };

    $("#w2layout-root").w2layout(this.layout);

    w2ui.layout.load("top", "/app-layout/header.html", null, () => {
      ReactDOM.render(<AppNav />, $("#sub-header-container").get(0));

    const isClientView = DesignerSetting.s.is_public === 'true';
      
      $("#app-title").html(
        window.apm.serialized.clientName +
        " " +
        window.apm.serialized.designName
      );
      $("#app-version").html(window.apm.serialized.version);

      $('#version-info').on('click', (e) => {
        if(!isClientView){
          const hasError = $(e.target).hasClass('error');
        
          if(hasError){
            return false;
          } 
          else {
            return window.apm.openLatestVersion();
          }
        }
      });

      if(isClientView){
        $('#version-info').toggle();
        $('#saving-status').toggle();
      }
      window.apm.setVersionInfo("fetchingInfo");
    });

    w2ui.layout.load("bottom", "/app-layout/footer.html");

    w2ui.layout.load("main", "/app-layout/main.html", null, () => {
      
      // set Start tab as active
      CanvasTabManager.setTab(rootModule.id);
      // sortable adds drag sorting of canvas tabs
      CanvasTabManager.initSortable();

      $("#tb_layout_main_toolbar_item_icon-note")
        .attr("draggable", "True")
        .on("dragstart", function (event) {
          const node = new NoteNodeModel().serialize();
          window.apm.cache.put(
            Constant.k.NAMESPACE_DATATRANSFER,
            "dragndrop",
            node
          );
        });

      $("#tb_layout_main_toolbar_item_icon-package")
        .attr("draggable", "True")
        .on("dragstart", function (event) {
          let node = CreateObjects.createModule();
          node.props.timeAddedToFlow = window.apm.timestamp;
          window.apm.cache.put(
            Constant.k.NAMESPACE_DATATRANSFER,
            "dragndrop",
            node
          );
          window.apm.postEventToCurrentView({
            target: "request-component-panel",
            event: node,
            toolBarNode: true,
            suppressPropertyPanel: true
          });
        });

      $("#tb_layout_main_toolbar_item_icon-mic")
        .attr("draggable", "True")
        .on("dragstart", function (event) {
          let node = CreateObjects.createInput();
          node.props.timeAddedToFlow = window.apm.timestamp;
          window.apm.cache.put(
            Constant.k.NAMESPACE_DATATRANSFER,
            "dragndrop",
            node
          );
          window.apm.postEventToCurrentView({
            target: "request-component-panel",
            event: node,
            toolBarNode: true,
            suppressPropertyPanel: true
          });
        });
      $("#tb_layout_main_toolbar_item_icon-message-square")
        .attr("draggable", "True")
        .on("dragstart", function (event) {
          let node = CreateObjects.createPrompt();
          node.props.timeAddedToFlow = window.apm.timestamp;
          window.apm.cache.put(
            Constant.k.NAMESPACE_DATATRANSFER,
            "dragndrop",
            node
          );
          window.apm.postEventToCurrentView({
            target: "request-component-panel",
            event: node,
            toolBarNode: true,
            fromToolBar: true,
            suppressPropertyPanel: true
          });
        });
      $("#tb_layout_main_toolbar_item_icon-cpu")
        .attr("draggable", "True")
        .on("dragstart", function (event) {
          let node = CreateObjects.createCustom();
          node.props.timeAddedToFlow = window.apm.timestamp;
          window.apm.cache.put(
            Constant.k.NAMESPACE_DATATRANSFER,
            "dragndrop",
            node
          );
          window.apm.postEventToCurrentView({
            target: "request-component-panel",
            event: node,
            toolBarNode: true,
            suppressPropertyPanel: true
          });
        });

      $("#value-zoom")
        .on("focus", function () {
          $(this).data("val", $(this).val());
          $(this).attr("placeholder", $(this).val());
          $(this).val("");
        })
        .on("blur", function () {
          const zoomValInput = $("#value-zoom");
          var val = zoomValInput.val();
          val = parseInt(val);
          if (isNaN(val)) {
            val = parseInt(zoomValInput.data("val"));
          }
          window.apm.postEventToCurrentView({
            target: "request-zoom-set",
            param: { zoom: val }
          });
        })
        .keypress(function (event) {
          if (event.keyCode === 13) {
            event.preventDefault();
            $(this).trigger("blur");
          } else if (event.keyCode >= 48 && event.keyCode <= 57) {
            return $(this).val().length < 3;
          } else {
            return false;
          }
        });

      // render all pages in application
      this.apm.reactDOMComponents.renderApplicationPages(window.apm.serialized);
      this.apm.initDisplayPrefs('init-disabled-elements');
    });

    // apply languages
    $("#tree_library")
      .find(".tree-title-text")
      .html(window.l.library.application);
    $("#tree_member")
      .find(".tree-title-text")
      .html(window.l.library.current_diagram);

    $("#tl_q").attr("placeholder", window.l.popup.search);
    $("#tl_q").attr("autocomplete", "off");
    $("#tl_q,#tm_q").keypress(function (event) {
      if (event.keyCode === 13) {
        event.preventDefault();
        $(this).trigger("blur");
      } else {
        return $(this).val();
      }
    });
    $("#tm_q").attr("placeholder", window.l.popup.search);
    
  }

  /**
   * Initialize layout and event handling for both treeviews
   */
  initTrees() {
    // TODO: find another place to initialze folders
    if(!window.apm.serialized.folders) {
      window.apm.serialized.folders = {};
      window.apm.serialized.folders.modules = [];
    }

    function isFolder(type) {
      return (
        type === "folders" ||
        type === "modules" ||
        type === "intents" ||
        type === "objects" ||
        type === "variables"
      );
    }
    function isDraggable(type) {
      return type !== "variable" && type !== "intent";
    }

    $("#tl_container")
      .jstree({
        core: {
          check_callback: true,
          data: [
            {
              id: "variables",
              text: window.l.library.variables,
              type: "variables",
              children: []
            },
            {
              id: "modules",
              text: window.l.library.modules,
              type: "modules",
              children: []
            },
            {
              id: "objects",
              text: window.l.library.objects,
              type: "objects",
              children: []
            },
            {
              id: "intents",
              text: window.l.library.intents,
              type: "intents",
              children: []
            }
          ],
          multiple: true,
          themes: {
            dots: false, // no connecting dots between dots
            ellipsis: false,
            stripes: false
          },
          expand_selected_onload: false,
          strings: {
            "New node": "Unnamed"
          }
        },
        types: {
          objects: {
            icon: "no-icon",
            valid_children: ["objects", "object"]
          },
          modules: {
            icon: "no-icon",
            valid_children: ["modules", "module"]
          },
          intents: {
            icon: "no-icon",
            valid_children: ["intents", "intent"]
          },
          variables: {
            icon: "no-icon",
            valid_children: ["variables", "variable"]
          },
          object: {
            icon: "/lib/feather/disc.svg",
            valid_children: []
          },
          module: {
            icon: "/lib/feather/package.svg",
            valid_children: []
          },
          intent: {
            icon: "/lib/feather/radio.svg",
            valid_children: []
          },
          variable: {
            icon: "/lib/feather/hash.svg",
            valid_children: []
          }
        },
        search: {
          show_only_matches: true,
          search_leaves_only: true
        },
        plugins: ["types", "search", "sort"]
      })
      .on("dblclick.jstree", function (e) {
        const openTabs = window.apm.getCanvasTabManager().getTabs().tabs;
        const node = $(this)
          .jstree()
          .get_node(e.target);

        if (node.type === "module") {
          if (!window.apm.hasPermission(node.id, "read")) {
            window.apm.notification.post("error", window.l.notification.no_permission);
            return;
          }

          // DEVDES2-1515: Don't open overlay of tab already open
          if(openTabs.findIndex(tab => tab.id === node.id) === -1){
            Utility.openApplicationOverlay('Loading Module...');  
          }
            
          // this time out allows the overlay to render before adding the tab
          setTimeout(() => {CanvasTabManager.addTab(node)}, 50);
        }
      })
      .on("select_node.jstree", 
        // debouncing so that select doesn't execute when user is dbclicking 
        _.debounce((e, data) => {
            window.apm.postEventToCurrentView({
              target: "request-clear-selection",
            })
            if (data.event && !isFolder(data.node.type)) {
              window.apm.postEventToCurrentView({
                target: "request-component-panel",
                event: data.node,
              })
            }
        }, 150)
      )
      .on("hover_node.jstree", function (_, data) {
        window.apm.printDebug("hover data", data, Constant.k.DEBUG_TREE);
        const anchor = $("#" + data.node.a_attr.id);
        // DEVDES2-1046: Disabled modules cannot be dragged and dropped onto canvas
        const isTargetModuleDisabledInFlow = window.apm.serialized.components.modules.find(mod => mod.id === window.apm.serialized.currentActiveTab).props.config.disabledInFlow;

        if (anchor.length) {
          if (!isFolder(data.node.type)) {
            anchor[0].draggable = "true";
          } else {
            anchor[0].draggable = "false";
          }
        }
        if (
          data.node.type === "module" &&
          window.apm.hasPermission(data.node.id, "read")
        ) {
          const node = window.apm.getNode(data.node.id);
          window.apm.postEventToCurrentView({
            target: "request-render-preview",
            node: node,
            domContext: anchor,
            isTargetModuleDisabledInFlow : isTargetModuleDisabledInFlow
          });
        }
      })
      .on("dehover_node.jstree", function () {
        window.apm.postEventToCurrentView({ target: "request-cancel-preview" });
      })
      .on("dragstart.jstree", function (e) {
        const isTargetModuleDisabledInFlow = window.apm.serialized.components.modules.find(mod => mod.id === window.apm.serialized.currentActiveTab).props.config.disabledInFlow;
        window.apm.printDebug("dragstart", e, Constant.k.DEBUG_TREE);
        window.apm.postEventToCurrentView({ target: "request-cancel-preview" });
        const node = $(this)
          .jstree()
          .get_node(e.target);

        
        // prevent dragndrop of disabled nodes to canvas unless current canvas is a disabled module
        if(node.li_attr.class === 'disabledInFlow'){
          // DEVDES2-1046: Disabled modules cannot be dragged and dropped onto canvas
          if(!isTargetModuleDisabledInFlow){
            e.preventDefault();
            e.stopPropagation();
            return;   
          }
        }

        if (!isFolder(node.type) && isDraggable(node.type)) {
          window.apm.cache.put(
            Constant.k.NAMESPACE_DATATRANSFER,
            "dragndrop",
            window.apm.getNode(node.id)
          );
        }
      })
      .on("contextmenu.jstree", function (e) {
        e.preventDefault();
        const node = $(this).jstree().get_node(e.target);
        const nodes = $(this).jstree().is_selected(e.target) ? $(this).jstree().get_selected(true) : [node];
        window.apm.postEventToCurrentView({
          target: "request-tree-menu",
          nodes: nodes,
          event: e
        });
        if (
          // Property panel does not exist for folders.
          !isFolder(node.type)
          // The intent dialog would cover the context menu.
          && node.type !== 'intent'
        ) {
          window.apm.postEventToCurrentView({
            target: "request-component-panel",
            event: node
          });
        }
      });
      
    this.apm.initDisplayPrefs('init-disabled-elements');

    $("#tm_container")
      .jstree({
        core: {
          check_callback: true,
          data: [
            // currentMod is the cutrently opened module in the canvas
            {
              id: "currentMod",
              text: "Current Module",
              type: "currentMod",
              state: { opened: true },
              children: []
            },
            {
              id: "variables",
              text: window.l.library.local_variables,
              type: "variables",
              state: { opened: true },
              children: []
            },
            {
              id: "modules",
              text: window.l.library.modules,
              type: "modules",
              state: { opened: true },
              children: []
            },
            {
              id: "objects",
              text: window.l.library.objects,
              type: "objects",
              state: { opened: true },
              children: []
            }
          ],
          multiple: false, // no multiselection
          themes: {
            dots: false // no connecting dots between dots
          }
        },
        types: {
          currentMod: {
            icon: "no-icon",
            valid_children: ["module"]
          },
          objects: {
            icon: "no-icon",
            valid_children: ["objects", "object"]
          },
          modules: {
            icon: "no-icon",
            valid_children: ["module"]
          },
          variables: {
            icon: "no-icon",
            valid_children: ["variable"]
          },
          object: {
            icon: "/lib/feather/disc.svg",
            valid_children: []
          },
          module: {
            icon: "/lib/feather/package.svg",
            valid_children: []
          },
          variable: {
            icon: "/lib/feather/hash.svg",
            valid_children: []
          }
        },
        search: {
          show_only_matches: true,
          search_leaves_only: true
        },
        plugins: ["types", "search", "sort"]
      })
      .on("dblclick.jstree", function (e) {
        // CM TODO: module versions and flowVersion: remove or refactor flowVersion
        const node = $(this)
          .jstree()
          .get_node(e.target);
        if (node.type === "module") {
          window.apm.postEventToCurrentView({
            target: "request-open-module",
            event: { 
              id: model.model.getID(),
              version: model.model.props.flowVersion || 1
          } 
          });
        }
      })
      .on("select_node.jstree", function (e, data) {
        // if not currentModule select and center node on canvas
        if (data.event && data.node.parent !== "currentMod") {
          window.apm.postEventToCurrentView({
            target: "request-select-matching",
            event: data.node
          });
        } 
        // if currentModule then nothing on canvas to select, just open property panel
        if(data.event && data.node.parent === "currentMod"){
          window.apm.postEventToCurrentView({
            target: "request-component-panel",
            event: data.node,
          })
        }
      })
      .on("hover_node.jstree", function (e, data) {
        const anchor = $("#" + data.node.a_attr.id);
        if (anchor.length) {
          anchor[0].draggable = "false";
        }
      })
      .on("deselect_all.jstree", function (e, data) { })
      .on("contextmenu.jstree", function (e) {
        e.preventDefault();
        const node = $(this)
          .jstree()
          .get_node(e.target);
        $(this)
          .jstree()
          .deselect_all();
        $(this)
          .jstree()
          .select_node(node);
        window.apm.postEventToCurrentView({
          target: "request-tree-menu",
          node: node,
          local: true,
          event: e
        });
      });

    $("#left-panels").onResize({
      localStorageId: "resize:left-panels",
      maxSize: 700,
      minSize: 220,
      side: "left"
    });

    const libraryTree = $("#tree_library");
    const memberTree = $("#tree_member");

    function updateTreeviews() {
      if (libraryTree.data("open")) {
        libraryTree
          .addClass("open")
          .find(".title-prefix-icon > i")
          .removeClass("icon-chevrons-down")
          .addClass("icon-chevrons-up");
      } else {
        libraryTree
          .removeClass("open")
          .find(".title-prefix-icon > i")
          .removeClass("icon-chevrons-up")
          .addClass("icon-chevrons-down");
      }
      if (memberTree.data("open")) {
        memberTree
          .addClass("open")
          .find(".title-prefix-icon > i")
          .removeClass("icon-chevrons-down")
          .addClass("icon-chevrons-up");
      } else {
        memberTree
          .removeClass("open")
          .find(".title-prefix-icon > i")
          .removeClass("icon-chevrons-up")
          .addClass("icon-chevrons-down");
      }
    }

    libraryTree.data("open", true);
    memberTree.data("open", true);
    updateTreeviews();

    libraryTree.find(".tree-title").on("click", function (event) {
      if ($(event.target).hasClass("form-button")) {
        window.apm.postEventToCurrentView({
          target: "request-tree-title-menu",
          type: "library",
          dom: $(event.target)
        });
        return;
      }
      const newOpen = !libraryTree.data("open");
      if (newOpen) {
        libraryTree.children(".tree-container").show();
      } else {
        libraryTree.children(".tree-container").hide();
      }
      libraryTree.data("open", newOpen);
      updateTreeviews();
    });

    memberTree.find(".tree-title").on("click", function (event) {
      if ($(event.target).hasClass("form-button")) {
        window.apm.postEventToCurrentView({
          target: "request-tree-title-menu",
          type: "member",
          dom: $(event.target)
        });
        return;
      }
      const newOpen = !memberTree.data("open");
      if (newOpen) {
        memberTree.children(".tree-container").show();
      } else {
        memberTree.children(".tree-container").hide();
      }
      memberTree.data("open", newOpen);
      updateTreeviews();
    });

    $("#tl_search").on("change keyup paste", function () {
      const searchInput = $("#tl_q");
      this.searchtimer = window.apm.timestamp;
      const refreshTree = () => {
        // refresh tree
        if (!libraryTree.data("open")) {
          libraryTree.children(".tree-container").show();
          libraryTree.data("open", true);
          updateTreeviews();
        }
        // refresh search field
        if (searchInput.val() && searchInput.val().length) {
          $(this)
            .find(".clear-button")
            .show();
        } else {
          $(this)
            .find(".clear-button")
            .hide();
        }
      };

      const debouncedSearch = _.debounce(searchInput => {
        $("#tl_container")
        .jstree(true)
        .search(searchInput.val());

        refreshTree();
     
    }, 150);
     
    // debouncing the search input event allows users to complete typing (200ms pause) before submitting search
    debouncedSearch(searchInput);

    });
    $("#tm_search").on("change keyup paste", function () {
      const searchInput = $("#tm_q");

      const debouncedSearch = _.debounce((searchInput) => {
        $("#tm_container").jstree(true).search(searchInput.val())

        if (!memberTree.data("open")) {
          memberTree.children(".tree-container").show()
          memberTree.data("open", true)
          updateTreeviews()
        }
        if (searchInput.val() && searchInput.val().length) {
          $(this).find(".clear-button").show()
        } else {
          $(this).find(".clear-button").hide()
        }
		  }, 100);

      debouncedSearch(searchInput);
    });

    $("#tl_search")
      .find(".clear-button")
      .on("click", function () {
        $("#tl_q").val("");
        $("#tl_search").trigger("change");
        $(this).hide();
      });
    $("#tm_search")
      .find(".clear-button")
      .on("click", function () {
        $("#tm_q").val("");
        $("#tm_search").trigger("change");
        $(this).hide();
      });

    $(window).on("resize", function () {
      updateTreeviews();
    });
  }
}
